import React, { useState, useEffect, useMemo } from "react";
import api from "../../api/api";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";

import PaginationControl from "../../utils/PaginationControl";
import { useNavigate } from "react-router-dom";
import NoticePreviewDialog from "../../director/components/NoticePreviewDialog";

// Icons
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

/* Helper: always send null values as "null" */
const buildQueryConfig = (statusFilter, page, pageSize, sortOrder) => ({
  params: {
    status: statusFilter === "All" ? null : statusFilter,
    email: null,
    Page: page,
    PageSize: pageSize,
    sortOrder: sortOrder === "newest" ? "desc" : "asc",
  },
  paramsSerializer: {
    serialize: (params) =>
      Object.entries(params)
        .map(
          ([k, v]) =>
            `${encodeURIComponent(k)}=${v === null ? "null" : encodeURIComponent(v)}`
        )
        .join("&"),
  },
});

const NoticesTable = () => {
  const [notices, setNotices] = useState([]);
  const [sortOrder, setSortOrder] = useState("newest");
  const [loading, setLoading] = useState(false);

  //  Default status now Pending
  const [statusFilter, setStatusFilter] = useState("Pending");

  const [selectedNotice, setSelectedNotice] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    notice: null,
    action: "",
  });

  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalNotices, setTotalNotices] = useState(0);
  const [buttonLoading, setButtonLoading] = useState({});
  const navigate = useNavigate();

  const showToast = (message, severity = "success") =>
    setToast({ open: true, message, severity });

  const handleCloseToast = () =>
    setToast((prev) => ({ ...prev, open: false }));

  // Fetch notices
  const fetchNotices = async (pg = 1, sz = 10) => {
    setLoading(true);
    try {
      const res = await api.get(
        "/api/Admin/GetApprovedorunApprovedNotices",
        buildQueryConfig(statusFilter, pg, sz, sortOrder)
      );

      let data = res.data?.data || [];
      data = data.map((n) => ({
        ...n,
        status: n.noticeStatus?.trim?.() || "Pending",
      }));

      setNotices(data);
      setTotalNotices(res.data?.count || data.length || 0);
    } catch (err) {
      console.error("Fetch error", err);
      showToast("Failed to load notices.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  fetchNotices(page, pageSize);
}, [page, pageSize, statusFilter, sortOrder]);



const handleApproveReject = async (noticeId, approve = true) => {
  setButtonLoading((prev) => ({ ...prev, [noticeId]: true }));

  try {
    // First update the notice status
    const res = await api.post("/api/Admin/ApproveOrRejectNotice", {
      noticeId,
      isApproved: approve,
    });

    if (res.data?.status?.toLowerCase() === "success") {

      // Second call - send approval/rejection email
      try {
        await api.post("/api/Admin/SendNoticeApproveEmail", {
          noticeId,
          isApproved: approve,
          rejectionReason: ""
        });
      } catch (emailErr) {
        console.error("Failed to send email:", emailErr);
        // Optional: Show a warning instead of failing the whole operation
        showToast(
          "Notice updated, but email could not be sent.",
          "warning"
        );
      }

      showToast(
        approve ? "Notice approved." : "Notice rejected.",
        "success"
      );

      await fetchNotices(page, pageSize);
    } else {
      showToast(res.data?.message || "Update failed.", "error");
    }
  } catch (err) {
    console.error("Approve/reject failed:", err);
    showToast("Something went wrong.", "error");
  } finally {
    setButtonLoading((prev) => ({ ...prev, [noticeId]: false }));
  }
};

  const handleDeleteConfirm = async (notice) => {
    setButtonLoading((prev) => ({ ...prev, [notice.noticeId]: true }));
    try {
      await api.delete("/api/NewCastingNotice/DeleteCastingNoticeDetails", {
        params: { noticeId: notice.noticeId },
      });
      showToast("Notice deleted.", "success");
      await fetchNotices(page, pageSize);
    } catch (err) {
      console.error("Delete failed:", err);
      showToast("Failed to delete.", "error");
    } finally {
      setButtonLoading((prev) => ({ ...prev, [notice.noticeId]: false }));
    }
  };

  const handleView = (notice) => {
    setSelectedNotice(notice);
    setViewDialogOpen(true);
  };

  const handleConfirm = async () => {
    const { notice, action } = confirmDialog;
    if (!notice) return;

    if (action === "approve") await handleApproveReject(notice.noticeId, true);
    else if (action === "reject") await handleApproveReject(notice.noticeId, false);
    else if (action === "delete") await handleDeleteConfirm(notice);

    setConfirmDialog({ open: false, notice: null, action: "" });
  };

  return (
    <>
      <style>{`
        .notice-title:hover {
          text-decoration: underline;
        }
      `}</style>

      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Manage Notices</Typography>

          <Box display="flex" gap={2}>
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
                <MenuItem value="All">All</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortOrder}
                label="Sort By"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
{notices.length > 0 ? (
  notices.map((n) => {
                      const isPending = n.status?.toLowerCase?.() === "pending";

                      return (
                        <TableRow key={n.noticeId}>
                          <TableCell>{n.noticeId}</TableCell>

                          {/*  Clickable title */}
                          <TableCell
                            className="notice-title"
                            style={{
                              cursor: "pointer"
                            }}
                            onClick={() => handleView(n)}
                          >
                            <Tooltip title="Click to preview">
                              <span>{n.projectTitle || n.title}</span>
                            </Tooltip>
                          </TableCell>

                          <TableCell>{n.contactName || n.directorName}</TableCell>

                          <TableCell>
                            <Typography
                              color={
                                n.status === "Approved"
                                  ? "success.main"
                                  : n.status === "Rejected"
                                  ? "error.main"
                                  : "warning.main"
                              }
                            >
                              {n.status}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            <Box display="flex" justifyContent="center" gap={1.5}>
                              <Tooltip title="Approve">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!isPending}
                                    onClick={() =>
                                      setConfirmDialog({
                                        open: true,
                                        notice: n,
                                        action: "approve",
                                      })
                                    }
                                  >
                                    <CheckCircleOutlineIcon
                                      color={isPending ? "success" : "disabled"}
                                      fontSize="small"
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              <Tooltip title="Reject">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!isPending}
                                    onClick={() =>
                                      setConfirmDialog({
                                        open: true,
                                        notice: n,
                                        action: "reject",
                                      })
                                    }
                                  >
                                    <HighlightOffIcon
                                      color={isPending ? "error" : "disabled"}
                                      fontSize="small"
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    navigate(`/post-casting-call/${n.noticeId}`, {
                                      state: { casting: n, noticeId: n.noticeId },
                                    })
                                  }
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setConfirmDialog({
                                      open: true,
                                      notice: n,
                                      action: "delete",
                                    })
                                  }
                                >
                                  <DeleteOutlineIcon color="error" fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No {statusFilter.toLowerCase()} notices found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <PaginationControl
              page={page}
              totalItems={totalNotices}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </>
        )}
      </Box>

      <NoticePreviewDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        notice={selectedNotice}
      />

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, notice: null, action: "" })}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to <strong>{confirmDialog.action}</strong> this notice?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, notice: null, action: "" })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={
              confirmDialog.action === "reject" || confirmDialog.action === "delete"
                ? "error"
                : "success"
            }
            onClick={handleConfirm}
          >
            {confirmDialog.action === "approve"
              ? "Approve"
              : confirmDialog.action === "reject"
              ? "Reject"
              : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NoticesTable;
